/**
 * Team data helpers.
 * Keeps selection/sorting logic reusable for the landing section
 * and a future dedicated team page.
 */

export function getLandingTeachers(teachers) {
  return teachers
    .filter((t) => t.active && t.showOnLanding)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

export function getTeamPageTeachers(teachers) {
  return teachers
    .filter((t) => t.active && t.showOnTeamPage)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

/**
 * Split an array into balanced rows for visual grid layout.
 * maxPerRow = max cards per row (default 3 for desktop).
 *
 * Examples (max 3):
 *   1 → [1]
 *   2 → [2]
 *   3 → [3]
 *   4 → [2,2]  (balanced) – could also be [4] if maxPerRow=4
 *   5 → [3,2]
 *   6 → [3,3]
 *
 * Examples (max 4):
 *   4 → [4]
 *   5 → [3,2]
 *   6 → [3,3]
 *   7 → [4,3]
 */
export function balancedRows(items, maxPerRow = 3) {
  const total = items.length;
  if (total === 0) return [];
  if (total <= maxPerRow) return [items];

  // For 4 items with max 3, prefer 2+2 over 3+1
  if (total === maxPerRow + 1) {
    const half = Math.ceil(total / 2);
    return [items.slice(0, half), items.slice(half)];
  }

  const rows = [];
  let i = 0;
  while (i < total) {
    const remaining = total - i;
    // If remaining fits in one row, push it
    if (remaining <= maxPerRow) {
      rows.push(items.slice(i));
      break;
    }
    // Avoid leaving 1 item alone in last row
    if (remaining === maxPerRow + 1) {
      const half = Math.ceil(remaining / 2);
      rows.push(items.slice(i, i + half));
      rows.push(items.slice(i + half));
      break;
    }
    rows.push(items.slice(i, i + maxPerRow));
    i += maxPerRow;
  }
  return rows;
}
